import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validates if an element is outside the blacklist, an error is returned if the control value is in the blacklist
 * @param blackList The blacklist of elements for which an error should be thrown
 * @returns null if the control's value is not in the blacklist, else a blackList: value error will be thrown
 */
export const blackListValidator = (blackList: any[]): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    // Validator will never activate if no elements are given
    if ((blackList?.length ?? 0) === 0) {
      return null;
    }

    const indexOfValue = blackList.indexOf(control.value);
    if (indexOfValue === -1) {
      return null;
    }

    return { blackList: blackList[indexOfValue] };
  };
};

/**
 * Validator to check if the start time is before the end time
 * @param startInputName The input name for the start time control
 * @param endInputName The input name for the end time control
 */
export const timeRangeValidator = (startInputName: string, endInputName: string): ValidatorFn => {
  return (formGroup: UntypedFormGroup): ValidationErrors | null => {
    const startTime = formGroup.get(startInputName)?.value?.[0];
    const endTime = formGroup.get(endInputName)?.value?.[0];

    if (startTime && endTime && startTime >= endTime) {
      return { timeRangeInvalid: true };
    }
    return null;
  };
};

/**
 * Validtor to check if the control value is not an string of only whitespace
 */
export const noWhiteSpaceValidator = (control: UntypedFormControl) => {
  return (control.value || '').trim().length ? null : { whitespace: true };
};

/**
 * Validates a US phone number: exactly 10 digits once formatting characters
 * (e.g. those added by an input mask, "(305) 555-1234") are ignored.
 * Empty values pass, so pair this with `Validators.required` when the field is mandatory.
 * @returns null if valid, else a `{ phone: true }` error
 */
export const phoneValidator = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return String(value).replace(/\D/g, '').length === 10 ? null : { phone: true };
};
